import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@getgreenline/blaze-ui";
import { UserPlus } from "lucide-react";
import type { OnboardedBrand } from "@/services/staff-brand-users.service";
import { getInitials } from "@/utils/strings";

type BrandUsersTableProps = {
  brands: OnboardedBrand[];
  onAssignUser: (brand: OnboardedBrand) => void;
};

function UserSummary({ users }: { users: OnboardedBrand["users"] }) {
  const visibleUsers = users.slice(0, 4);
  const remainingUsers = users.length - visibleUsers.length;

  return (
    <div
      className="tw:flex tw:items-center tw:gap-3"
      aria-label={`${users.length} assigned ${users.length === 1 ? "user" : "users"}`}
    >
      <div className="tw:flex tw:-space-x-2" aria-hidden="true">
        {visibleUsers.map((user) => (
          <Avatar
            key={user.email}
            className="tw:size-8 tw:border-2 tw:border-background"
            title={user.email}
          >
            <AvatarFallback className="tw:text-xs">
              {getInitials(user.email)}
            </AvatarFallback>
          </Avatar>
        ))}
        {remainingUsers > 0 ? (
          <div className="tw:flex tw:size-8 tw:items-center tw:justify-center tw:rounded-full tw:border-2 tw:border-background tw:bg-muted tw:text-xs tw:font-medium">
            +{remainingUsers}
          </div>
        ) : null}
      </div>
      <span className="tw:text-sm tw:text-muted-foreground">
        {users.length} {users.length === 1 ? "user" : "users"}
      </span>
    </div>
  );
}

export function BrandUsersTable({
  brands,
  onAssignUser,
}: BrandUsersTableProps) {
  return (
    <div className="tw:overflow-x-auto tw:rounded-lg tw:border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Brand</TableHead>
            <TableHead>Users</TableHead>
            <TableHead>Running campaigns</TableHead>
            <TableHead>
              <span className="tw:sr-only">Actions</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {brands.map((brand) => (
            <TableRow key={brand.id}>
              <TableCell>
                <div className="tw:flex tw:min-w-56 tw:items-center tw:gap-3">
                  <Avatar className="tw:size-9">
                    {brand.avatarImageUrl ? (
                      <AvatarImage
                        src={brand.avatarImageUrl}
                        alt={`${brand.name} icon`}
                      />
                    ) : null}
                    <AvatarFallback>{getInitials(brand.name)}</AvatarFallback>
                  </Avatar>
                  <div className="tw:min-w-0">
                    <p className="tw:font-medium">{brand.name}</p>
                    <p className="tw:max-w-md tw:truncate tw:text-xs tw:text-muted-foreground">
                      Owner: {brand.owner?.email ?? "No owner assigned"}
                    </p>
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <UserSummary users={brand.users} />
              </TableCell>
              <TableCell>{brand.runningCampaignCount}</TableCell>
              <TableCell className="tw:text-right">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onAssignUser(brand)}
                >
                  <UserPlus className="tw:size-4" />
                  Add user
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
